import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Capacitor } from "@capacitor/core";
import posthog from "posthog-js";
import { FaPencilAlt, FaRegLightbulb, FaRegFileAlt } from "react-icons/fa";
import { FiAlertTriangle, FiChevronRight } from "react-icons/fi";

import api from "../../api/apiHelper";
import { submitProblemAttempt } from "../../services/problemService";
import { RenderMarkdownWithLatex } from "../../utils/utils";
import { invalidateApiCache } from "../../utils/apiCache";
import { useWindowSize } from "../../hooks/useWindowSize";
import { useAndroidSafeArea } from "../../hooks/useAndroidSafeArea";
import orzelLogo from "../../img/orzel_logo_192.png";

import PageTestPage from "../molecules/PageTestPage";
import HintAndFormulas from "../molecules/HintAndFormulas";
import HintPopup from "../molecules/HintPopup";
import ScratchpadModal from "../molecules/ScratchpadModal";
import SolutionStepper from "../molecules/SolutionStepper";
import AiChatPopup from "../molecules/AiChatPopup";
import TutorialCoachmark, { TUTORIAL_HIGHLIGHT } from "../molecules/TutorialCoachmark";
import { TUTORIAL_SKETCH } from "../molecules/tutorialSketch";

// Ekran jest swiadomie zbudowany jak SolveProblem — ta sama chrome, biala karta,
// zarowka, karta wzorow, FAB brudnopisu i stopka. Samouczek ma nauczyc TEGO
// ekranu, wiec kazda roznica wizualna byla by bledem.
//
// Model prowadzenia: ODKRYWANIE. Na starcie wszystko jest wygaszone; element
// omawiany w danym kroku swieci, a kazdy juz odkryty zostaje normalny i dziala.
// Nie wracamy do wygaszania rzeczy, ktore user juz poznal.
const STEP = {
    INTRO: 0,
    FORMULAS: 1,
    HINT: 2,
    SCRATCHPAD: 3,
    ANSWER: 4,
    ELO: 5,
    SOURCE: 6,
    SOLUTION: 7,
    NEXT: 8,
};

// Orzel AI nie ma wlasnego kroku: podswietlenie i dymek zachecaja do klikniecia,
// a samouczek ma nauczyc ekranu, nie wypchnac usera w czat (ktory na tym zadaniu
// nie ma nic sensownego do powiedzenia). Przycisk zostaje widoczny i klikalny.
const TUTORIAL_CHAT_GREETING =
    "Jestem Orzeł AI. Odpowiem na Twoje pytania o rozwiązanie. To zadanie jest z samouczka, więc porozmawiamy na serio przy pierwszym prawdziwym zadaniu w sesji.";
const TUTORIAL_CHAT_REPLY =
    "Przy prawdziwym zadaniu odpowiem na to konkretnie - tam mam całą jego treść i rozwiązanie. Dokończ samouczek i zacznij robić zadania!";

// target — selektor elementu, przy ktorym staje dymek (dymek sam wybiera gore
// albo dol, zeby go nie zaslonic); place wymusza strone tam, gdzie automat
// wybiera zle. cta — przycisk "dalej" dla krokow bez interakcji.
//
// Kroki interaktywne NIE maja osobnego wiersza "kliknij X" — czynnosc jest
// ostatnim zdaniem tresci. Osobny blok konkurowal o uwage z podswietlonym
// elementem, a ten i tak pulsuje na zloto.
const STEP_CONFIG = {
    [STEP.INTRO]: {
        title: "To jest Twój ekran nauki",
        body: "Każde zadanie wygląda dokładnie tak. Pokażę Ci po kolei, co masz pod ręką.",
        target: null,
        cta: "Dobra, pokaż",
    },
    [STEP.FORMULAS]: {
        title: "Karta wzorów CKE",
        body: "Ta sama karta, którą dostaniesz na maturze, przefiltrowana pod to zadanie. Otwórz ją.",
        target: '[data-tut="formulas"]',
    },
    [STEP.HINT]: {
        title: "Żarówka w rogu",
        body: "Wskazówka nie zdradzi wyniku, ale pomoże Ci ruszyć z zadaniem. Zobacz ją teraz.",
        target: '[data-tut="hint"]',
        // Zarowka siedzi w gornym rogu karty: dymek pod nia przykrylby wlasnie
        // rozwinieta karte wzorow i tresc zadania. Swieci, wiec i tak ja widac.
        place: "screen-bottom",
    },
    [STEP.SCRATCHPAD]: {
        title: "Brudnopis",
        body: "Jeśli nie masz pod ręką kartki i długopisu, możesz skorzystać z Brudnopisu. Zajrzyj do środka.",
        target: '[data-tut="scratchpad"]',
    },
    [STEP.ANSWER]: {
        title: "Twoja kolej",
        body: "Tym razem podpowiem Ci, która odpowiedź jest poprawna - zaznacz ją.",
        target: '[data-tut="answers"]',
    },
    [STEP.ELO]: {
        title: "Punkty za zadania",
        body: "U góry masz swoje punkty ELO i ELO zadania. Za każde rozwiązane zadanie Twoje ELO rośnie. Na podstawie tych punktów dobieramy kolejne zadania do Twojego poziomu.",
        target: '[data-tut="elo"]',
        cta: "Jasne",
        // Licznik ELO jest na samej gorze karty — dymek pod nim zaslonilby
        // zadanie i wlasnie zaznaczona odpowiedz.
        place: "screen-bottom",
    },
    [STEP.SOURCE]: {
        title: "To było zadanie typu maturalnego",
        body: "Tutaj widzisz, w których arkuszach CKE pojawił się ten typ zadania. W Matematly rozwiązujesz tylko zadania typu maturalnego.",
        target: '[data-tut="source"]',
        cta: "Rozumiem",
    },
    [STEP.SOLUTION]: {
        title: "Rozwiązanie krok po kroku",
        body: "Do każdego zadania masz pełne rozwiązanie krok po kroku, a pod nim Orła AI, który wytłumaczy Ci szczegóły rozwiązania. Otwórz je.",
        target: '[data-tut="solution"]',
    },
    [STEP.NEXT]: {
        title: "Powodzenia!",
        body: "To wszystko, co musisz wiedzieć. Powodzenia w nauce do matury!",
        target: '[data-tut="next"]',
        // Nad przyciskiem jest rozwiazanie i Orzel — dymek ma stanac pod nim.
        place: "below",
    },
};

const HIGHLIGHT = TUTORIAL_HIGHLIGHT;

// Brudnopis w samouczku jest do OBEJRZENIA, nie do pracy — user nie ma tu nic
// liczyc, ma tylko wiedziec, ze to miejsce istnieje i jak z niego wrocic.
const SCRATCHPAD_HINT = "To zadanie treningowe - nic nie musisz pisać.";

// Dashboard zapisuje `userStatusBeforeSession` PRZED przejsciem do samouczka, wiec
// zadanie rozwiazane tutaj nie istnieje w tym zdjeciu stanu. EndSessionStats liczy
// z niego postep celu (`problems_solved_today + rozwiazane w sesji`) i bez tej
// synchronizacji gubi dokladnie jedno zadanie: cel domkniety na serwerze pokazywal
// sie jako 2/3. Samouczek dzieje sie przed sesja, wiec jego wynik nalezy do stanu
// "przed" — nadpisujemy wartosciami z backendu, ktory jest tu jedynym zrodlem prawdy.
const syncBeforeSessionSnapshot = (data, fieldId, eloAfterAttempt) => {
    // Drugie zdjecie stanu: `available_fields` (zapisywane przy ladowaniu Dashboardu)
    // niesie licznik zadan i ELO per dzial i sluzy za baze kamieni milowych. Bez
    // tej samej korekty kamien odpalilby sie o jedno zadanie za pozno.
    try {
        const rawFields = localStorage.getItem("available_fields");
        if (rawFields && fieldId) {
            const fields = JSON.parse(rawFields);
            if (Array.isArray(fields)) {
                const entry = fields.find((f) => f.field_id === fieldId);
                if (entry) {
                    entry.total_solved_per_field = (entry.total_solved_per_field || 0) + 1;
                    if (typeof eloAfterAttempt === "number") {
                        entry.user_field_elo = eloAfterAttempt;
                    }
                    localStorage.setItem("available_fields", JSON.stringify(fields));
                }
            }
        }
    } catch (e) {
        console.error("Tutorial: nie udalo sie zsynchronizowac available_fields", e);
    }

    // Trzecie zdjecie stanu: `fullUserStatsBeforeSession` (mapa field_id -> {elo,
    // total_problems_solved} z radar_chart). Na nim stoi awans rangi i punkt
    // startowy ELO w statystykach dzialu — bez korekty podsumowanie sesji
    // pokazywaloby ELO nizsze o przyrost z samouczka. Klucze mapy przechodza
    // przez JSON, wiec sa stringami.
    try {
        const rawStats = localStorage.getItem("fullUserStatsBeforeSession");
        if (rawStats && fieldId) {
            const stats = JSON.parse(rawStats);
            const entry = stats?.[String(fieldId)] || stats?.[fieldId];
            if (entry) {
                entry.total_problems_solved = (entry.total_problems_solved || 0) + 1;
                if (typeof eloAfterAttempt === "number") entry.elo = eloAfterAttempt;
                localStorage.setItem("fullUserStatsBeforeSession", JSON.stringify(stats));
            }
        }
    } catch (e) {
        console.error("Tutorial: nie udalo sie zsynchronizowac statystyk dzialow", e);
    }

    try {
        const raw = localStorage.getItem("userStatusBeforeSession");
        if (!raw) return;
        const before = JSON.parse(raw);
        if (typeof data?.problems_solved_today === "number") {
            before.problems_solved_today = data.problems_solved_today;
        }
        if (typeof data?.problems_solved_total === "number") {
            before.problems_solved_total = data.problems_solved_total;
        }
        if (typeof data?.current_streak === "number") {
            before.current_streak = data.current_streak;
        }
        if (typeof data?.streak_freezes === "number") {
            before.streak_freezes = data.streak_freezes;
        }
        localStorage.setItem("userStatusBeforeSession", JSON.stringify(before));
    } catch (e) {
        console.error("Tutorial: nie udalo sie zsynchronizowac stanu przed sesja", e);
    }
};

const getVisualLengthEstimate = (text) => {
    if (!text) return 0;
    return text.replace(/\\[a-zA-Z]+|\$|{|}/g, "").length;
};

const TutorialProblem = () => {
    const navigate = useNavigate();
    const { width } = useWindowSize();
    const { paddingBottom } = useAndroidSafeArea();
    const isIOS = Capacitor.getPlatform() === "ios";

    // /tutorial?force=1 — podglad samouczka na koncie z historia (nie zapisuje proby).
    // Tylko w devie: w buildzie produkcyjnym tryb jest martwy po obu stronach
    // (backend tez sprawdza DEBUG), wiec nikt nie wejdzie w samouczek z URL-a.
    const [searchParams] = useSearchParams();
    const isPreview =
        process.env.NODE_ENV !== "production" && searchParams.get("force") === "1";

    const [problem, setProblem] = useState(null);
    const [step, setStep] = useState(STEP.INTRO);
    const [isScratchpadOpen, setIsScratchpadOpen] = useState(false);
    const [isSkipConfirmOpen, setIsSkipConfirmOpen] = useState(false);
    const [isHintPopupOpen, setIsHintPopupOpen] = useState(false);
    const [sketch, setSketch] = useState(TUTORIAL_SKETCH);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showSolution, setShowSolution] = useState(false);
    const [eloAfter, setEloAfter] = useState(null);
    const [startTime] = useState(() => Math.floor(Date.now() / 1000));

    // Wlasny, offline'owy czat: samouczek nie pali zapytania do modelu ani limitu
    // AI usera. Orzel tylko sie przedstawia i odsyla do prawdziwej sesji.
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [chatHistory, setChatHistory] = useState([
        { role: "model", parts: [{ text: TUTORIAL_CHAT_GREETING }] },
    ]);
    const handleTutorialMessage = useCallback((msg) => {
        setChatHistory((prev) => [
            ...prev,
            { role: "user", parts: [{ text: msg }] },
            { role: "model", parts: [{ text: TUTORIAL_CHAT_REPLY }] },
        ]);
    }, []);

    const accountStatus = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("userStatus")) || null;
        } catch (e) {
            return null;
        }
    }, []);

    const stickyBottomStyle = isIOS ? "1rem" : "1.5rem";
    const fabBottomPosition = isIOS
        ? `calc(9rem + ${paddingBottom})`
        : `calc(8rem + ${paddingBottom})`;

    // Samouczek wchodzi przed pierwsza sesja, wiec wyjscie z niego to wejscie
    // w sesje — chyba ze ktos trafil tu bez przygotowanej paczki zadan.
    const exitToSession = useCallback(
        (replace = true) => {
            if (isPreview) {
                navigate("/app", { replace });
                return;
            }
            const hasSession = !!localStorage.getItem("problemsData");
            navigate(hasSession ? "/solve-problem" : "/app", { replace });
        },
        [navigate, isPreview]
    );

    useEffect(() => {
        let cancelled = false;
        let prefetched = null;
        if (!isPreview) {
            try { prefetched = JSON.parse(sessionStorage.getItem("tutorialPrefetch")); } catch (e) {}
        }
        const request = prefetched
            ? Promise.resolve({ data: prefetched })
            : api.get(isPreview ? "/tutorial-problem/?force=1" : "/tutorial-problem/");
        request
            .then((res) => {
                if (cancelled) return;
                sessionStorage.removeItem("tutorialPrefetch");
                if (!res.data?.tutorial) {
                    exitToSession();
                    return;
                }
                setProblem(res.data.tutorial);
                posthog.capture("tutorial_started");
            })
            .catch(() => {
                if (!cancelled) exitToSession();
            });
        return () => {
            cancelled = true;
        };
    }, [exitToSession, isPreview]);

    useEffect(() => {
        if (problem) posthog.capture("tutorial_step_viewed", { step });
    }, [step, problem]);

    const answers = useMemo(() => {
        if (!problem) return [];
        return [
            { text: problem.true_answer, isCorrect: true },
            { text: problem.false_answer1, isCorrect: false },
            { text: problem.false_answer2, isCorrect: false },
            { text: problem.false_answer3, isCorrect: false },
        ].sort(() => Math.random() - 0.5);
    }, [problem]);

    const gridClass = useMemo(() => {
        if (!answers.length) return "grid-cols-1";
        const dynamicThreshold = width < 768 ? 15 : 40;
        const isAnyAnswerLong = answers.some(
            (a) => getVisualLengthEstimate(a.text) > dynamicThreshold
        );
        return isAnyAnswerLong ? "grid-cols-1" : "grid-cols-2";
    }, [answers, width]);

    const sources = problem?.linked_matura_problems || [];

    // Realna sciezka samouczka: kroki bez elementu na ekranie odpadaja (brak wzorow,
    // brak zrodla maturalnego). Z niej lecza sie kropki postepu i przejscie "dalej",
    // wiec nie da sie utknac na kroku, ktoremu nie ma w co kliknac.
    const flow = useMemo(() => {
        const hasFormulas = !!problem?.linked_formulas?.length;
        return [
            STEP.INTRO,
            ...(hasFormulas ? [STEP.FORMULAS] : []),
            STEP.HINT,
            STEP.SCRATCHPAD,
            STEP.ANSWER,
            STEP.ELO,
            ...(sources.length > 0 ? [STEP.SOURCE] : []),
            STEP.SOLUTION,
            STEP.NEXT,
        ];
    }, [problem, sources.length]);

    const nextOf = useCallback(
        (current) => flow[Math.min(flow.indexOf(current) + 1, flow.length - 1)],
        [flow]
    );
    const goNext = useCallback(() => setStep(nextOf), [nextOf]);

    // Wyjscie jest, ale za potwierdzeniem: samouczka nie da sie odzyskac (bramka
    // to `ProblemSolve.exists()`, wiec znika po pierwszym rozwiazanym zadaniu),
    // a bez bramki "Pomin" bylby odruchowym "zamknij to" na pierwszym ekranie.
    const handleSkipClick = useCallback(() => {
        posthog.capture("tutorial_skip_clicked", { step });
        setIsSkipConfirmOpen(true);
    }, [step]);

    const handleSkipConfirm = useCallback(() => {
        posthog.capture("tutorial_skipped", { step });
        exitToSession();
    }, [step, exitToSession]);

    const handleSkipCancel = useCallback(() => {
        posthog.capture("tutorial_skip_cancelled", { step });
        setIsSkipConfirmOpen(false);
    }, [step]);

    const handleAnswerClick = async (answer) => {
        if (step !== STEP.ANSWER || !answer.isCorrect || selectedAnswer) return;

        setSelectedAnswer(answer.text);

        // Pelny przyrost ELO: mimo prowadzenia nie zglaszamy hinta ani rozwiazania.
        // W podgladzie proby nie zapisujemy, zeby nie podbijac ELO na koncie testowym.
        if (!isPreview) {
            try {
                const data = await submitProblemAttempt({
                    problem_id: problem.problem_id,
                    field_id: problem.field_id,
                    is_solved: true,
                    is_hint_used: false,
                    is_solution_viewed: false,
                    start_time: startTime,
                    end_time: Math.floor(Date.now() / 1000),
                });
                const eloAfterAttempt =
                    typeof data?.attempt_elo_change === "number"
                        ? problem.user_field_elo + data.attempt_elo_change
                        : undefined;
                if (eloAfterAttempt !== undefined) setEloAfter(eloAfterAttempt);
                syncBeforeSessionSnapshot(data, problem.field_id, eloAfterAttempt);
                posthog.capture("tutorial_answer_submitted");
            } catch (e) {
                console.error("Tutorial: blad zapisu proby", e);
            }
            invalidateApiCache("user_dashboard_data");
            invalidateApiCache("user_stats");
        }

        setTimeout(goNext, 900);
    };

    const handleFinish = () => {
        posthog.capture("tutorial_completed");
        exitToSession();
    };

    // Bez tego przez czas requestu mignelaby biel — samouczek wchodzi zaraz po
    // overlayu ladowania sesji, wiec przejscie ma byc niewidoczne.
    if (!problem) return <div className="min-h-[100dvh] w-full bg-[#F7F7F7]" />;

    // Odkrywanie: przed swoim krokiem element jest wygaszony, w swoim kroku swieci,
    // po nim wraca do normalnego wygladu i dziala jak w prawdziwej sesji.
    // keepPosition: dla elementow, ktore maja wlasne pozycjonowanie (zarowka jest
    // `absolute`, FAB brudnopisu `fixed`). Dorzucenie `relative` zbijaloby je z
    // pozycji — obie klasy ustawiaja `position`, a w Tailwind `relative` jest
    // zdefiniowany pozniej, wiec wygrywa. Zarowka ladowala wtedy po lewej,
    // a FAB w ogole znikal z ekranu.
    // Wygaszenie 40%, nie 25% — element ma czytac sie jako "jeszcze nieaktywny",
    // a nie jako uszkodzony/znikajacy ekran.
    // hideBefore — element plywajacy nad trescia (FAB) zanim przyjdzie jego kolej
    // znika calkiem, zamiast sie wygaszac: wygaszony i tak nachodzil na dymek.
    const reveal = (elementStep, { keepPosition = false, hideBefore = false } = {}) => {
        if (step < elementStep)
            return hideBefore ? "opacity-0 pointer-events-none" : "opacity-40 pointer-events-none";
        if (step === elementStep) {
            // Zadnego border-radius tutaj — ring podaza za ksztaltem elementu.
            // `rounded-xl` z podswietlenia robilo z okraglego FAB-a kwadrat.
            const stack = keepPosition ? "" : " relative z-10";
            return `${HIGHLIGHT}${stack}`;
        }
        return "";
    };

    const config = STEP_CONFIG[step];
    const stepIndex = Math.max(0, flow.indexOf(step));
    // "Pomin" dopiero od drugiego kroku: na powitalnym ekranie bylby odruchowym
    // "zamknij to", zanim user w ogole wie, ze to 40 sekund. Na ostatnim kroku
    // nie ma juz czego pomijac. Liczymy po pozycji w `flow`, bo kroki bez elementu
    // na ekranie z niego wypadaja.
    const canSkip = stepIndex >= 1 && step !== STEP.NEXT;

    // `screen-bottom` to ustepstwo dla waskich ekranow: tam dymek zakotwiczony
    // pod zarowka albo pod licznikiem ELO przykrywa cala biala karte. Na tablecie
    // miejsca jest dosc, wiec kotwiczymy normalnie — dymek jest wezszy niz karta
    // i po nowemu przesuwa sie poziomo za celem, wiec zaslania tylko jej rog.
    // Wymuszenie "below" (ostatni krok) zostaje na kazdym ekranie.
    const preferredPlace =
        config.place === "screen-bottom" && width >= 768 ? undefined : config.place;
    const answered = !!selectedAnswer;
    const dailyGoal = accountStatus?.problems_daily_goal || 3;
    const dailyGoalProgress = Math.min(
        100,
        ((accountStatus?.problems_solved_today || 0) / dailyGoal) * 100 + 5
    );
    const displayedElo = eloAfter !== null ? eloAfter : problem.user_field_elo;

    return (
        <PageTestPage
            showNav={true}
            accountStatus={accountStatus}
            dailyGoalProgress={dailyGoalProgress}
        >
            <ScratchpadModal
                isOpen={isScratchpadOpen}
                onClose={() => {
                    setIsScratchpadOpen(false);
                    if (step === STEP.SCRATCHPAD) goNext();
                }}
                problem={problem}
                initialPaths={sketch}
                onSave={(_problemId, paths) => setSketch(paths)}
                coach={
                    step === STEP.SCRATCHPAD
                        ? {
                              title: STEP_CONFIG[STEP.SCRATCHPAD].title,
                              body: `${SCRATCHPAD_HINT} Wróć przyciskiem ZAPISZ.`,
                              stepIndex: Math.max(0, flow.indexOf(STEP.SCRATCHPAD)),
                              stepCount: flow.length,
                          }
                        : null
                }
            />

            {/* --- PRZYCISK OTWIERANIA (FAB) --- */}
            <button
                data-tut="scratchpad"
                data-coach-avoid={step >= STEP.SCRATCHPAD ? "" : undefined}
                onClick={() => setIsScratchpadOpen(true)}
                aria-hidden={step < STEP.SCRATCHPAD}
                tabIndex={step < STEP.SCRATCHPAD ? -1 : 0}
                className={`fixed right-4 z-40 bg-[#233855] text-white p-4 rounded-full shadow-[0_4px_14px_0_rgba(0,0,0,0.39)] hover:bg-[#345081] transition-all border-2 border-[#E09F2E] ${reveal(
                    STEP.SCRATCHPAD,
                    { keepPosition: true, hideBefore: true }
                )}`}
                style={{ bottom: fabBottomPosition }}
                aria-label="Otwórz brudnopis"
            >
                <FaPencilAlt size={20} />
            </button>

            <div className="flex flex-col max-w-2xl mx-auto w-full flex-grow px-5 lg:px-0">
                <div className="relative w-full mt-[4vh] lg:mt-[8vh]">
                    {/* --- BIAŁA KARTA (TREŚĆ ZADANIA) --- */}
                    <div className="bg-white rounded-xl lg:rounded-[3rem] shadow-[4px_4px_12px_rgba(0,0,0,0.30)] border border-gray-200 pt-8 pb-6 px-5 lg:px-14 w-full overflow-hidden relative">
                        {/* --- IKONA ŻARÓWKI (POPUP HINT) --- */}
                        <button
                            data-tut="hint"
                            onClick={() => {
                                setIsHintPopupOpen(true);
                                if (step === STEP.HINT) goNext();
                            }}
                            className={`absolute top-4 right-4 text-[#E09F2E] hover:text-[#b88226] hover:scale-110 transition-all p-2 z-10 rounded-full ${reveal(
                                STEP.HINT,
                                { keepPosition: true }
                            )}`}
                            aria-label="Zobacz wskazówkę"
                        >
                            <FaRegLightbulb size={24} strokeWidth={1} className="drop-shadow-sm" />
                        </button>

                        <div className="w-full space-y-6">
                            {/* ELO HEADER — odkrywany dopiero po rozwiązaniu zadania */}
                            <div
                                data-tut="elo"
                                className={`w-full flex justify-center items-baseline flex-wrap gap-x-3 sm:gap-x-4 gap-y-2 text-xs font-apotek lg:mt-[0.5vh] rounded-xl ${reveal(
                                    STEP.ELO
                                )}`}
                            >
                                <div className="flex items-baseline gap-2">
                                    <span className="uppercase font-medium text-xs text-[#233855]/60 tracking-wide">
                                        Twoje ELO
                                    </span>
                                    <span className="text-[#233855] font-bold text-xs flex items-baseline gap-1">
                                        {displayedElo}
                                        {eloAfter !== null && (
                                            <span className="text-[#007D00] text-[0.65rem] font-bold">
                                                +{eloAfter - problem.user_field_elo}
                                            </span>
                                        )}
                                    </span>
                                </div>
                                <span className="text-[#233855]/30 font-light text-xs">vs</span>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-[#233855] font-bold text-xs">
                                        {problem.problem_elo}
                                    </span>
                                    <span className="uppercase font-medium text-xs text-[#233855]/60 tracking-wide">
                                        ELO Zadania
                                    </span>
                                </div>
                            </div>

                            {/* PYTANIE — zawsze czytelne, to kontekst całego ekranu */}
                            {/* Punkty przed trescia — dokladnie jak w SolveProblem. */}
                            <p className="text-base lg:text-normal font-normal text-gray-900 text-center min-h-[50px] flex items-center justify-center lg:mt-[0.5vh]">
                                <RenderMarkdownWithLatex
                                    text={
                                        !problem.is_mobile && problem.points_assigned
                                            ? `(${problem.points_assigned} pkt) ${problem.question}`
                                            : problem.question
                                    }
                                />
                            </p>

                            <div className="space-y-4 pt-1">
                                {/* KARTA WZORÓW */}
                                {problem.linked_formulas?.length > 0 && (
                                    step > STEP.FORMULAS ? (
                                        <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                            <HintAndFormulas
                                                hintText={null}
                                                formulas={problem.linked_formulas}
                                            />
                                        </div>
                                    ) : (
                                        <button
                                            data-tut="formulas"
                                            onClick={() => step === STEP.FORMULAS && goNext()}
                                            className={`group w-full flex items-center gap-3 bg-white border border-[#E09F2E]/40 px-4 py-3 rounded-xl shadow-sm hover:shadow-md hover:border-[#E09F2E] transition-all active:scale-[0.98] ${reveal(
                                                STEP.FORMULAS
                                            )}`}
                                        >
                                            <div className="w-9 h-9 rounded-lg bg-[#233855] flex items-center justify-center flex-shrink-0">
                                                <FaRegFileAlt size={16} className="text-[#E09F2E]" />
                                            </div>
                                            <div className="flex-1 text-left min-w-0">
                                                <span className="block text-sm font-bold text-[#233855] font-apotek leading-tight">
                                                    Karta Wzorów
                                                </span>
                                                <span className="block text-xs text-gray-400 leading-tight">
                                                    Otwórz wzory do zadania
                                                </span>
                                            </div>
                                            <div className="w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center flex-shrink-0 transition-colors">
                                                <FiChevronRight className="w-4 h-4 text-gray-400" />
                                            </div>
                                        </button>
                                    )
                                )}

                                {/* ODPOWIEDZI */}
                                {/* Ring tylko na poprawnej odpowiedzi — obramowanie calej
                                    siatki nie mowilo userowi, w co ma kliknac. */}
                                <div
                                    data-tut="answers"
                                    className={`grid ${gridClass} gap-2 w-full ${
                                        step < STEP.ANSWER
                                            ? "opacity-40 pointer-events-none"
                                            : step === STEP.ANSWER
                                            ? "relative z-10"
                                            : ""
                                    }`}
                                >
                                    {answers.map((answer, index) => {
                                        const isPicked = selectedAnswer === answer.text;
                                        let buttonClass =
                                            "flex items-center justify-center rounded-lg text-[0.8rem] text-slate-100 px-3 py-1.5 font-normal tracking-wider shadow-md transition-colors duration-200 disabled:cursor-not-allowed min-h-[20px] w-full";
                                        if (isPicked)
                                            buttonClass += " bg-[#007D00] ring-2 ring-offset-2 ring-[#007D00]";
                                        else if (answered) buttonClass += " bg-[#233855] opacity-40";
                                        else if (step === STEP.ANSWER && answer.isCorrect)
                                            buttonClass += ` bg-[#233855] ${HIGHLIGHT}`;
                                        else buttonClass += " bg-[#233855]";

                                        return (
                                            <motion.button
                                                key={index}
                                                onClick={() => handleAnswerClick(answer)}
                                                disabled={step !== STEP.ANSWER || answered}
                                                className={buttonClass}
                                                whileTap={
                                                    step === STEP.ANSWER && answer.isCorrect
                                                        ? { scale: 0.96 }
                                                        : undefined
                                                }
                                            >
                                                <RenderMarkdownWithLatex text={answer.text} />
                                            </motion.button>
                                        );
                                    })}
                                </div>

                                {/* ROZWIĄZANIE */}
                                {!showSolution && (
                                    <button
                                        data-tut="solution"
                                        onClick={() => {
                                            if (step !== STEP.SOLUTION) return;
                                            setShowSolution(true);
                                            goNext();
                                        }}
                                        className={`w-full text-xs font-uppercase font-apotek shadow-md tracking-widest bg-[#F0F0F0] border border-gray-300 text-gray-600 hover:bg-gray-50 px-4 py-1.5 rounded-lg font-medium transition-all ${reveal(
                                            STEP.SOLUTION
                                        )}`}
                                    >
                                        ROZWIĄZANIE
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* --- /BIAŁA KARTA --- */}

                    {/* POZA KARTĄ: rozwiązanie krok po kroku + Orzeł AI */}
                    {showSolution && (
                        <div className="w-full mt-4 space-y-3 animate-in fade-in zoom-in duration-300">
                            <SolutionStepper solutionText={problem.solution || "Brak rozwiązania."} />
                            {/* Orzel: bez podswietlenia i bez wlasnego kroku — ma byc
                                zauwazony, nie wyklikany. Klikniecie otwiera czat z
                                gotowa wiadomoscia, bez zapytania do modelu. */}
                            <button
                                onClick={() => setIsChatOpen(true)}
                                className="group w-full flex items-center gap-3 bg-white border border-[#E09F2E]/40 px-4 py-3 rounded-xl shadow-sm hover:shadow-md hover:border-[#E09F2E] transition-all active:scale-[0.98]"
                            >
                                <div className="w-9 h-9 rounded-full bg-[#233855] flex items-center justify-center flex-shrink-0 overflow-hidden">
                                    <img src={orzelLogo} alt="Orzeł AI" className="w-full h-full object-cover" />
                                </div>
                                <div className="flex-1 text-left min-w-0">
                                    <span className="block text-sm font-bold text-[#233855] font-apotek leading-tight">
                                        Zapytaj Orła AI
                                    </span>
                                    <span className="block text-xs text-gray-400 leading-tight">
                                        Wyjaśni krok po kroku
                                    </span>
                                </div>
                                <div className="w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center flex-shrink-0 transition-colors">
                                    <FiChevronRight className="w-4 h-4 text-gray-400" />
                                </div>
                            </button>
                        </div>
                    )}

                    {/* PRZYCISK KOLEJNE ZADANIE */}
                    {step === STEP.NEXT && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="w-full pt-6 flex justify-center"
                        >
                            <button
                                data-tut="next"
                                onClick={handleFinish}
                                className={`flex w-full lg:w-[25rem] justify-center rounded-lg bg-[#233855] px-3 py-2.5 text-xs lg:text-xs font-apotek tracking-widest text-slate-100 hover:bg-[#345081] relative z-10 ${HIGHLIGHT}`}
                            >
                                KOLEJNE ZADANIE
                            </button>
                        </motion.div>
                    )}

                </div>
                {/* Na ostatnim kroku dokladamy zapas do przewiniecia: dymek staje POD
                    przyciskiem, wiec przycisk musi dac sie wyscrollowac wyzej. */}
                <div
                    className={`flex-shrink-0 ${step === STEP.NEXT ? "h-[32vh]" : "h-[10vh]"}`}
                />
            </div>
            <div className="flex-grow" />

            {/* --- STOPKA: "NA PODSTAWIE" --- */}
            <div className="sticky w-full pt-3 z-30 lg:bottom-[5vh]" style={{ bottom: stickyBottomStyle }}>
                <div className="max-w-xl mx-auto space-y-5 p-2">
                    {sources.length > 0 && (
                        <div
                            data-tut="source"
                            className={`w-full text-center text-xs text-gray-500 italic transition-all rounded-xl ${reveal(
                                STEP.SOURCE
                            )} ${step === STEP.SOURCE ? "py-2 bg-white" : ""}`}
                        >
                            Na podstawie: {sources.map((pb) => pb.source).join(", ")}
                        </div>
                    )}
                </div>
            </div>

            {/* --- DYMEK PROWADZĄCY (NAKŁADKA) ---
                Kotwiczy sie przy omawianym elemencie i sam wybiera strone (nad/pod),
                zeby go nie zaslonic — stad selektory `data-tut` w tym pliku.
                z-30 trzyma go pod FAB-em (z-40) i pod nawigacja (z-50). */}
            <TutorialCoachmark
                targetSelector={config.target}
                title={config.title}
                body={config.body}
                cta={config.cta}
                onCta={goNext}
                onSkip={canSkip ? handleSkipClick : null}
                preferredPlace={preferredPlace}
                stepIndex={stepIndex}
                stepCount={flow.length}
                fallbackBottom={`calc(4.75rem + ${paddingBottom})`}
            />

            {/* POTWIERDZENIE POMINIECIA — wzorzec dialogow z Settings.js.
                z-[9995], bo nawigacja (z-50) renderuje sie PO tresci strony i bez tego
                malowalaby sie na dialogu. Bezpieczna opcja jest wypelniona, "pomin" to
                przycisk obrysowany — domyslna sciezka, nie ukrycie wyjscia. */}
            {isSkipConfirmOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[9995] p-4"
                    onClick={handleSkipCancel}
                >
                    <div
                        className="bg-white rounded-2xl shadow-lg p-6 max-w-sm w-full"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="tutorial-skip-title"
                    >
                        <FiAlertTriangle className="h-14 w-14 text-[#E09F2E] mx-auto mb-4" />
                        <h3
                            id="tutorial-skip-title"
                            className="text-xl font-apotek tracking-widest text-[#233855] text-center"
                        >
                            Pominąć samouczek?
                        </h3>
                        <p className="mt-3 text-sm text-gray-600 text-center">
                            Pokazuję go tylko raz. Po pierwszym zadaniu nie
                            będzie już można do niego wrócić.
                        </p>
                        {/* Akcja nieodwracalna stoi NIZEJ w hierarchii: klikalny tekst,
                            nie drugi przycisk. Obrysowany przycisk na cala szerokosc
                            czytal sie jak rownorzedny wybor. Pole dotyku dalej 44 px. */}
                        <div className="mt-6 flex flex-col items-center">
                            <button
                                type="button"
                                onClick={handleSkipCancel}
                                className="w-full px-6 py-3 text-sm font-apotek tracking-widest rounded-lg bg-[#233855] text-white hover:bg-[#345081] shadow-md transition-colors"
                            >
                                WRACAM DO SAMOUCZKA
                            </button>
                            <button
                                type="button"
                                onClick={handleSkipConfirm}
                                className="mt-1 px-4 min-h-[44px] text-sm text-gray-500 underline underline-offset-2 hover:text-gray-700 transition-colors"
                            >
                                Pomiń mimo to
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <AiChatPopup
                isOpen={isChatOpen}
                onClose={() => setIsChatOpen(false)}
                chatHistory={chatHistory}
                onSendMessage={handleTutorialMessage}
                isResponding={false}
            />
            <HintPopup
                isOpen={isHintPopupOpen}
                onClose={() => setIsHintPopupOpen(false)}
                hintText={problem.hint}
            />
        </PageTestPage>
    );
};

export default TutorialProblem;
